import { calculateDistanceBetweenTwoPointsSphericalLawOfCosines } from '@deckee/utilities';

/** [longitude, latitude], as GeoJSON stores it. */
export type Coordinate = [number, number];

export interface TripLineParts {
  /** Runs of two or more points drawn as a solid line. */
  solid: Coordinate[][];
  /** Pairs of points further apart than the gap distance, drawn as gaps. */
  gaps: [Coordinate, Coordinate][];
}

/**
 * Splits a trip's points wherever two consecutive points are more than
 * `gapDistanceMetres` apart, so the app can draw that leg as a gap instead
 * of as if the boat was tracked along it. A gap distance of 0 or less, or one
 * that is not a number, draws the whole trip solid.
 */
export const splitTripAtGaps = (
  coordinates: Coordinate[],
  gapDistanceMetres: number,
): TripLineParts => {
  if (!(gapDistanceMetres > 0)) {
    return { solid: coordinates.length > 1 ? [coordinates] : [], gaps: [] };
  }

  const solid: Coordinate[][] = [];
  const gaps: [Coordinate, Coordinate][] = [];
  let run: Coordinate[] = [];
  const endRun = () => {
    if (run.length > 1) solid.push(run);
    run = [];
  };

  coordinates.forEach((coordinate, index) => {
    const previous = coordinates[index - 1];
    if (previous) {
      const distance = calculateDistanceBetweenTwoPointsSphericalLawOfCosines(
        previous[1],
        previous[0],
        coordinate[1],
        coordinate[0],
        'm',
      );
      if (distance !== null && distance > gapDistanceMetres) {
        endRun();
        gaps.push([previous, coordinate]);
      }
    }
    run.push(coordinate);
  });
  endRun();

  return { solid, gaps };
};
